import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  ArrowLeft,
  Users,
  Search,
  RotateCcw,
  Mail,
  Sparkles,
  X,
  SlidersHorizontal,
  ArrowUpDown,
} from "lucide-react";
import {
  CustomerDirectoryItem,
  CustomerDirectoryResponse,
  CustomerSource,
  SourceCatalogItem,
} from "../../../../Data/customerDirectory.dto";
import { getCustomerDirectoryAPI } from "../../../../Redux/ApiCalls/Dashboard/CustomerDirectoryApi";
import MarketplaceEmptyState, {
  MarketplaceLoadingSpinner,
} from "../Marketplace/MarketplaceEmptyState";
import {
  ListPanel,
  ListHeader,
  ListTitle,
  CountBadge,
  TableWrap,
  PaymentsTable,
} from "../../../atoms/Dashboard/CustomerTransaction/transactionTableStyles";
import Pagination from "../../../atoms/Dashboard/HomePage/Pagination";
import { showErrorToast, showSuccessToast } from "../../../atoms/Utlis/Toast";
import { device } from "../../../atoms/Utlis/media";

const DEFAULT_SOURCE_CATALOG: SourceCatalogItem[] = [
  { type: "scheduled_event", label: "Scheduled Event" },
  { type: "booked_package", label: "Booked Package" },
  { type: "order", label: "Order" },
  { type: "quotation_customer", label: "Quotation Customer" },
  { type: "event_inquiry", label: "Event Inquiry" },
  { type: "lead", label: "Lead" },
  { type: "invoice", label: "Invoice" },
  { type: "album", label: "Album" },
  { type: "payment", label: "Payment" },
  { type: "marketplace_booking", label: "Marketplace Booking" },
  { type: "legacy_quotation", label: "Legacy Quotation" },
];

const SOURCE_COLORS: Record<
  string,
  { bg: string; text: string; border: string }
> = {
  scheduled_event: {
    bg: "rgba(14, 165, 233, 0.12)",
    text: "#0284c7",
    border: "rgba(14, 165, 233, 0.28)",
  },
  quotation_customer: {
    bg: "rgba(236, 72, 153, 0.1)",
    text: "#db2777",
    border: "rgba(236, 72, 153, 0.25)",
  },
  event_inquiry: {
    bg: "rgba(59, 130, 246, 0.1)",
    text: "#2563eb",
    border: "rgba(59, 130, 246, 0.25)",
  },
  lead: {
    bg: "rgba(245, 158, 11, 0.12)",
    text: "#d97706",
    border: "rgba(245, 158, 11, 0.25)",
  },
  booked_package: {
    bg: "rgba(16, 185, 129, 0.1)",
    text: "#059669",
    border: "rgba(16, 185, 129, 0.25)",
  },
  order: {
    bg: "rgba(147, 51, 234, 0.1)",
    text: "#7e22ce",
    border: "rgba(147, 51, 234, 0.25)",
  },
  invoice: {
    bg: "rgba(14, 165, 233, 0.1)",
    text: "#0284c7",
    border: "rgba(14, 165, 233, 0.25)",
  },
  album: {
    bg: "rgba(201, 77, 170, 0.12)",
    text: "#c94daa",
    border: "rgba(201, 77, 170, 0.25)",
  },
  payment: {
    bg: "rgba(34, 197, 94, 0.12)",
    text: "#16a34a",
    border: "rgba(34, 197, 94, 0.25)",
  },
  marketplace_booking: {
    bg: "rgba(99, 102, 241, 0.1)",
    text: "#4f46e5",
    border: "rgba(99, 102, 241, 0.25)",
  },
  legacy_quotation: {
    bg: "rgba(107, 114, 128, 0.12)",
    text: "#4b5563",
    border: "rgba(107, 114, 128, 0.25)",
  },
};

interface GroupedSource {
  type: string;
  label: string;
  count: number;
}

function getGroupedSources(sources: CustomerSource[] = []): GroupedSource[] {
  const map: Record<string, { label: string; count: number }> = {};
  for (const s of sources) {
    if (!map[s.type]) {
      map[s.type] = { label: s.label || s.type, count: 0 };
    }
    map[s.type].count++;
  }
  return Object.entries(map).map(([type, val]) => ({
    type,
    label: val.label,
    count: val.count,
  }));
}

function formatCustomerPhone(
  phone?: string | null,
  countryCode?: string | null
): string | null {
  if (!phone) return null;
  const clean = phone.trim();
  if (!clean) return null;

  const code = (countryCode || "+91").trim();
  const codeDigits = code.replace(/^\+/, "");

  // If phone already starts with "+<country_code>" (e.g. "+917955845956" or "+91 7955845956")
  if (clean.startsWith(`+${codeDigits}`)) {
    const digits = clean.slice(`+${codeDigits}`.length).trim();
    return `${code} ${digits}`;
  }

  // If phone starts with "+" with a different country code
  if (clean.startsWith("+")) {
    return clean;
  }

  // If phone starts with country code without '+' (e.g. "917573810452" where length > 10)
  if (clean.startsWith(codeDigits) && clean.length > 10) {
    const digits = clean.slice(codeDigits.length).trim();
    return `${code} ${digits}`;
  }

  return `${code} ${clean}`;
}

const StudioCustomerDirectoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState<CustomerDirectoryItem[]>([]);
  const [sourceCatalog, setSourceCatalog] = useState<SourceCatalogItem[]>(
    DEFAULT_SOURCE_CATALOG
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");

  const isFilterActive = searchQuery.trim() !== "" || sourceFilter !== "all";

  const fetchCustomers = useCallback(
    async (pageToFetch: number) => {
      setLoading(true);
      try {
        const sourceParam = sourceFilter !== "all" ? sourceFilter : undefined;
        const isFiltering = searchQuery.trim() !== "" || sourceFilter !== "all";
        const effectivePageSize = isFiltering ? 10000 : pageSize;
        const res: CustomerDirectoryResponse = await getCustomerDirectoryAPI({
          page: isFiltering ? 1 : pageToFetch,
          page_size: effectivePageSize,
          search: searchQuery.trim() || undefined,
          source: sourceParam,
        });

        if (res && Array.isArray(res.results)) {
          setCustomers(res.results);
          setTotalCount(res.count ?? 0);
          if (Array.isArray(res.source_catalog) && res.source_catalog.length > 0) {
            setSourceCatalog(res.source_catalog);
          }
        } else {
          setCustomers([]);
          setTotalCount(0);
        }
      } catch (err: any) {
        showErrorToast(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load customer directory."
        );
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, sourceFilter, pageSize]
  );

  // Fetch data on search or filter change
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchCustomers(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, sourceFilter, fetchCustomers]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchCustomers(newPage);
  };

  const handleResetFilters = () => {
    setSearchQuery("");
    setSourceFilter("all");
    setSortBy("recent");
    setCurrentPage(1);
  };

  // Client side sorting for current page if requested
  const sortedCustomers = useMemo(() => {
    const items = [...customers];
    if (sortBy === "name_asc") {
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortBy === "name_desc") {
      items.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    } else if (sortBy === "sources_count") {
      items.sort(
        (a, b) =>
          (b.sources?.length || b.source_types?.length || 0) -
          (a.sources?.length || a.source_types?.length || 0)
      );
    }
    return items;
  }, [customers, sortBy]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageContainer>
      {/* Top Hero Section */}
      <Hero>
        <HeroGlow />
        <HeroRow>
          <HeroLeft>
            <BackButton
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <ArrowLeft size={18} />
            </BackButton>
            <HeroText>
              <HeroBadge>
                <Sparkles size={11} />
                <span>Studio Customer Directory</span>
              </HeroBadge>
              <PageTitle className="linear-text">Customer Directory</PageTitle>
              <PageSubtitle>
                Consolidated list of customers merged across all events, bookings,
                leads, quotations, invoices & albums, deduplicated by phone.
              </PageSubtitle>
            </HeroText>
          </HeroLeft>

          <HeroRight>
            <CountBadgeWrap>
              <CountNumber>{totalCount}</CountNumber>
              <CountLabel>Total Customers</CountLabel>
            </CountBadgeWrap>
          </HeroRight>
        </HeroRow>
      </Hero>

      {/* Main List Panel matching Payment Management Transactions */}
      <ListPanel>
        <ListHeader>
          <ListTitle>Studio Customers List</ListTitle>
          {totalCount > 0 && (
            <CountBadge>
              {isFilterActive
                ? `${totalCount} record${totalCount !== 1 ? "s" : ""} found`
                : `${totalCount} customer${totalCount !== 1 ? "s" : ""}`}
            </CountBadge>
          )}
        </ListHeader>

        {/* Filter Control Bar */}
        <FilterControlBar>
          <SearchInputBox>
            <SearchIconWrap>
              <Search size={16} />
            </SearchIconWrap>
            <SearchInputField
              type="text"
              placeholder="Search by customer name, phone number, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <ClearSearchBtn
                type="button"
                onClick={() => setSearchQuery("")}
                aria-label="Clear search"
              >
                <X size={14} />
              </ClearSearchBtn>
            )}
          </SearchInputBox>

          <FilterGroupRow>
            {/* Source Filter Select */}
            <SelectWrapper>
              <SelectIconWrap>
                <SlidersHorizontal size={14} />
              </SelectIconWrap>
              <FilterSelect
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <option value="all">All Sources</option>
                {sourceCatalog.map((cat) => (
                  <option key={cat.type} value={cat.type}>
                    {cat.label}
                  </option>
                ))}
              </FilterSelect>
            </SelectWrapper>

            {/* Sort By Select */}
            <SelectWrapper>
              <SelectIconWrap>
                <ArrowUpDown size={14} />
              </SelectIconWrap>
              <FilterSelect
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="recent">Recently Active</option>
                <option value="sources_count">Most Sources First</option>
                <option value="name_asc">Name: A to Z</option>
                <option value="name_desc">Name: Z to A</option>
              </FilterSelect>
            </SelectWrapper>

            {/* Reset Button */}
            {isFilterActive && (
              <ResetButton
                type="button"
                onClick={handleResetFilters}
                title="Reset all filters"
              >
                <RotateCcw size={13} />
                <span>Reset</span>
              </ResetButton>
            )}
          </FilterGroupRow>
        </FilterControlBar>

        {/* Active Filter Chips */}
        {isFilterActive && (
          <ActiveChipsRow>
            <ActiveChipsLabel>Active filters:</ActiveChipsLabel>
            {searchQuery.trim() && (
              <FilterChip>
                <span>Search: "{searchQuery.trim()}"</span>
                <button type="button" onClick={() => setSearchQuery("")}>
                  <X size={12} />
                </button>
              </FilterChip>
            )}
            {sourceFilter !== "all" && (
              <FilterChip>
                <span>
                  Source:{" "}
                  {sourceCatalog.find((c) => c.type === sourceFilter)?.label ||
                    sourceFilter}
                </span>
                <button type="button" onClick={() => setSourceFilter("all")}>
                  <X size={12} />
                </button>
              </FilterChip>
            )}
            <ClearAllLink type="button" onClick={handleResetFilters}>
              Clear all
            </ClearAllLink>
          </ActiveChipsRow>
        )}

        {/* Table Content or Empty State */}
        {loading ? (
          <MarketplaceLoadingSpinner padding="70px 22px" />
        ) : customers.length === 0 ? (
          <MarketplaceEmptyState
            padding="48px 0 32px"
            icon={<Users size={38} strokeWidth={1.5} />}
            title={
              isFilterActive
                ? "No matching customers found"
                : "No customer records found"
            }
            description={
              isFilterActive
                ? "No customers match your current search query or source filter. Try clearing your filters."
                : "Consolidated customer records from events, inquiries, leads, quotations, invoices, orders and albums will appear here."
            }
            hint={{
              icon: <Sparkles size={13} />,
              text: "All customers are automatically deduplicated by their 10-digit mobile number",
            }}
          />
        ) : (
          <TableWrap>
            <PaymentsTable $minWidth={780}>
              <thead>
                <tr>
                  <th style={{ width: "24%", minWidth: "180px" }}>
                    <span>Customer Name</span>
                  </th>
                  <th style={{ width: "18%", minWidth: "160px" }}>
                    <span>Phone Number</span>
                  </th>
                  <th style={{ width: "22%", minWidth: "180px" }}>
                    <span>Email</span>
                  </th>
                  <th style={{ width: "36%", minWidth: "240px" }}>
                    <span>Origin Sources</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCustomers.map((c) => {
                  const groupedSources = getGroupedSources(c.sources);
                  const fullPhone = formatCustomerPhone(c.phone, c.country_code);

                  return (
                    <TableRow key={c.id}>
                      {/* Customer Name */}
                      <td>
                        <CustomerCell>
                          <AvatarIcon>
                            {c.name ? (
                              c.name.charAt(0).toUpperCase()
                            ) : (
                              <Users size={14} />
                            )}
                          </AvatarIcon>
                          <CustomerNameCol>
                            <NameText>{c.name || "Unnamed Customer"}</NameText>
                          </CustomerNameCol>
                        </CustomerCell>
                      </td>

                      {/* Phone Number */}
                      <td>
                        {fullPhone ? (
                          <PhoneText>{fullPhone}</PhoneText>
                        ) : (
                          <DashText>—</DashText>
                        )}
                      </td>

                      {/* Email */}
                      <td>
                        {c.email ? (
                          <EmailText href={`mailto:${c.email}`}>
                            <Mail size={12} />
                            <span>{c.email}</span>
                          </EmailText>
                        ) : (
                          <DashText>—</DashText>
                        )}
                      </td>

                      {/* Origin Sources */}
                      <td>
                        <SourcesPillWrap>
                          {groupedSources.map((g) => {
                            const colors = SOURCE_COLORS[g.type] || {
                              bg: "rgba(23, 23, 23, 0.06)",
                              text: "#171717",
                              border: "rgba(23, 23, 23, 0.12)",
                            };
                            return (
                              <SourcePillBadge
                                key={g.type}
                                $bg={colors.bg}
                                $color={colors.text}
                                $border={colors.border}
                              >
                                <span>{g.label}</span>
                                {g.count > 1 && (
                                  <span className="count">({g.count})</span>
                                )}
                              </SourcePillBadge>
                            );
                          })}
                        </SourcesPillWrap>
                      </td>
                    </TableRow>
                  );
                })}
              </tbody>
            </PaymentsTable>
          </TableWrap>
        )}

        {/* Pagination */}
        {!isFilterActive && totalPages > 1 && totalCount > 0 && (
          <PaginationWrapper>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </PaginationWrapper>
        )}
      </ListPanel>
    </PageContainer>
  );
};

export default StudioCustomerDirectoryPage;

/* ==================== STYLES ==================== */

const PageContainer = styled.div`
  width: 100%;
  padding: 24px;
  font-family: Urbanist, sans-serif;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;

  @media ${device.tablet} {
    padding: 16px;
  }
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 22px 28px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.16) 0%,
    rgba(206, 86, 176, 0.12) 55%,
    rgba(53, 183, 200, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.15);
  box-shadow: 0 8px 28px rgba(201, 77, 170, 0.08);
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -80px;
  right: -5%;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 201, 81, 0.35) 0%, transparent 70%);
`;

const HeroRow = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  color: rgba(23, 23, 23, 0.72);
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.3);
    color: #c94daa;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(201, 77, 170, 0.15);
  }
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 5px;
  margin-bottom: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(201, 77, 170, 0.18);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #c94daa;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.25;
  color: #171717;
`;

const PageSubtitle = styled.p`
  margin: 2px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.62);
  max-width: 620px;
`;

const HeroRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const CountBadgeWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-width: 120px;
  padding: 10px 18px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(201, 77, 170, 0.2);
  box-shadow: 0 4px 16px rgba(201, 77, 170, 0.1);
`;

const CountNumber = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #c94daa;
  line-height: 1.2;
  text-align: center;
`;

const CountLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-align: center;
`;

/* Filter Controls matching TransactionList */

const FilterControlBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SearchInputBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  min-width: 280px;
  flex: 1;
  max-width: 480px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 12px;
  padding: 0 12px;
  transition: all 0.15s ease;

  &:focus-within {
    border-color: rgba(201, 77, 170, 0.5);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.08);
  }
`;

const SearchIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(23, 23, 23, 0.4);
`;

const SearchInputField = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  padding: 10px 8px;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: #171717;

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: rgba(23, 23, 23, 0.38);
  }
`;

const ClearSearchBtn = styled.button`
  border: none;
  background: rgba(23, 23, 23, 0.06);
  border-radius: 999px;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.5);

  &:hover {
    background: rgba(23, 23, 23, 0.12);
    color: #171717;
  }
`;

const FilterGroupRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SelectIconWrap = styled.div`
  position: absolute;
  left: 12px;
  pointer-events: none;
  display: flex;
  align-items: center;
  color: rgba(23, 23, 23, 0.45);
`;

const FilterSelect = styled.select`
  appearance: none;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.08);
  border-radius: 12px;
  padding: 9px 30px 9px 34px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #171717;
  cursor: pointer;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: rgba(201, 77, 170, 0.5);
    background: #fff;
  }

  &:hover {
    border-color: rgba(201, 77, 170, 0.3);
  }
`;

const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border-radius: 10px;
  border: 1px solid rgba(201, 77, 170, 0.25);
  background: rgba(201, 77, 170, 0.08);
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.16);
  }
`;

const ActiveChipsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 4px;
`;

const ActiveChipsLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.45);
`;

const FilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(201, 77, 170, 0.08);
  border: 1px solid rgba(201, 77, 170, 0.2);
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #c94daa;

  button {
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    color: #c94daa;
    display: flex;
    align-items: center;
  }
`;

const ClearAllLink = styled.button`
  border: none;
  background: none;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.5);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: #e44862;
  }
`;

/* Table Rows & Cells */

const TableRow = styled.tr`
  transition: background 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.03);
  }
`;

const CustomerCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AvatarIcon = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(144.01deg, rgba(255, 201, 81, 0.9) 0%, rgba(206, 86, 176, 0.9) 100%);
  color: #fff;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(201, 77, 170, 0.2);
`;

const CustomerNameCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const NameText = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #171717;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PhoneText = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  white-space: nowrap;
`;

const EmailText = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.7);
  text-decoration: none;

  &:hover {
    color: #c94daa;
    text-decoration: underline;
  }
`;

const DashText = styled.span`
  color: rgba(23, 23, 23, 0.35);
  font-weight: 500;
`;

const SourcesPillWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  width: 100%;
`;

const SourcePillBadge = styled.span<{
  $bg: string;
  $color: string;
  $border: string;
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  border: 1px solid ${(p) => p.$border};
  white-space: nowrap;

  .count {
    opacity: 0.85;
    font-size: 10px;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 12px;
  border-top: 1px solid rgba(23, 23, 23, 0.06);
`;
